import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../material.module';
import { SpacerService } from '../../../services/spacer.service';

export interface SpacerTestAppendNoteDialogData {
  slurryId: number;
  testInputMasterId: number;
  originalNote: string;
  testType: string; // e.g., 'Spacer Compatibility', 'Spacer SSST', etc.
}

@Component({
  selector: 'app-spacer-test-append-note-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './spacer-test-append-note-dialog.component.html',
  styleUrls: ['./spacer-test-append-note-dialog.component.css']
})
export class SpacerTestAppendNoteDialogComponent {
  noteToAppend = '';
  isWorking = false;
  errorMessage = '';

  get formattedOldNote(): string {
    if (!this.data.originalNote) {
      return '';
    }
    
    // Split by newlines, reverse to show newest first, and rejoin
    return this.data.originalNote
      .split(/\r?\n/)
      .filter(line => line.trim().length > 0)
      .reverse()
      .join('\n');
  }

  constructor(
    private readonly spacerService: SpacerService,
    private readonly dialogRef: MatDialogRef<SpacerTestAppendNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: SpacerTestAppendNoteDialogData
  ) { }

  onAppend(): void {
    const note = this.noteToAppend.trim();
    if (!note) {
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    this.spacerService.appendSpacerTestNote(this.data.testInputMasterId, note).subscribe({
      next: (response) => {
        this.isWorking = false;
        this.dialogRef.close({ success: true, updatedNotes: response.updatedNotes });
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to append note. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onClear(): void {
    const shouldClear = window.confirm('Are you sure you want to clear all old Notes?');
    if (!shouldClear) {
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    this.spacerService.clearSpacerTestNote(this.data.testInputMasterId).subscribe({
      next: (response) => {
        this.isWorking = false;
        this.dialogRef.close({ success: true, updatedNotes: response.updatedNotes });
      },
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to clear notes. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  private getErrorMessage(error: HttpErrorResponse, defaultMessage: string): string {
    if (error.error?.message) {
      return error.error.message;
    }
    if (error.error?.title) {
      return error.error.title;
    }
    if (typeof error.error === 'string') {
      return error.error;
    }
    return defaultMessage;
  }
}
